import { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, Star, FileCheck, FileX, X } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { SkeletonRows } from '../../components/skeleton';
import { StatusBadge } from '../../components/status-badge';
import { Pagination } from '../../components/pagination';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { LiveOpsMap } from './live-ops-map';
import { documentHref } from '../../api/media';
import { extractErr } from '../../api/error-message';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useUrlTab } from '../../services/attention';

const PER_PAGE = 15;

// Wired to the delivery-partner admin endpoints (GET /admin/delivery-partners,
// detail, document verify, approve/reject/suspend/reactivate). The Zones tab
// (pincode boundaries) and Live Ops Map are built in later phases.

interface PartnerRow {
  id: string;
  fullName: string;
  phoneE164: string;
  email: string | null;
  vehicleType: string | null;
  availabilityStatus: string;
  kycStatus: string;
  isActive: boolean;
  ratingAvg: number;
  ratingCount: number;
  createdAt: string;
}

interface PartnerDoc {
  id: string;
  docType: string;
  fileUrl: string;
  verified: boolean;
  rejectionReason: string | null;
}

interface PartnerBank {
  id: string;
  accountHolderName: string;
  accountNumberLast4: string;
  ifsc: string;
  bankName: string | null;
  isPrimary: boolean;
  verificationStatus: string;
}

interface PartnerDetail extends PartnerRow {
  servicePincodes: string[];
  documents: PartnerDoc[];
  bankAccounts: PartnerBank[];
}

const STATUS_TABS = [
  { key: 'SUBMITTED', label: 'Approval queue' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'ALL', label: 'All' },
] as const;
type StatusKey = (typeof STATUS_TABS)[number]['key'];

const initials = (name: string) => name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

/** Display-only: `SUBMITTED` → `Submitted`. The raw value stays the key. */
function sentenceCase(s: string): string {
  const spaced = s.replace(/_/g, ' ').trim();
  if (/^[A-Z]{2,4}$/.test(spaced)) return spaced;
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function DeliveryPage() {
  // In the URL (?tab=) so the dashboard / sidebar can open a specific queue.
  const [status, setStatus] = useUrlTab<StatusKey>(STATUS_TABS.map((t) => t.key), 'SUBMITTED');
  const [q, setQ] = useState('');
  // Server-searched: the list is paged by the API, so the query goes there and
  // matches every column below (name, phone, vehicle, KYC, availability, rating).
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [rows, setRows] = useState<PartnerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PartnerDetail | null>(null);
  const [drawerBusy, setDrawerBusy] = useState(false);
  const { confirm, prompt } = useConfirm();

  // Only the newest request may write rows: typing fires several, and a slow
  // early one must not overwrite the results of the query now in the box.
  const latestRequest = useRef(0);
  const load = async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const params: Record<string, string | number> = { status, page, limit: PER_PAGE };
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get<{ data: PartnerRow[]; pagination: { total: number } }>('/admin/delivery-partners', { params });
      if (requestId !== latestRequest.current) return;
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  // Back to page 1 whenever the tab or search changes, so a search made on
  // page 4 does not silently show nothing.
  useEffect(() => { setPage(1); }, [status, debouncedQ]);
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, debouncedQ, page]);

  const openDetail = async (id: string) => {
    try {
      const { data } = await api.get<PartnerDetail>(`/admin/delivery-partners/${id}`);
      setSelected(data);
    } catch (err) {
      toast.error(extractErr(err));
    }
  };

  const refreshDetail = async (id: string) => {
    const { data } = await api.get<PartnerDetail>(`/admin/delivery-partners/${id}`);
    setSelected(data);
  };

  const verifyDoc = async (docId: string, verified: boolean) => {
    if (!selected) return;
    let reason: string | null = null;
    if (!verified) {
      const doc = selected.documents.find((d) => d.id === docId);
      reason = await prompt({
        title: 'Reject document?',
        description: `${selected.fullName} sees the reason and has to upload ${doc ? `their ${sentenceCase(doc.docType)}` : 'this document'} again before they can be approved.`,
        label: 'Reason for rejection',
        placeholder: 'e.g. The licence has expired',
        required: false,
        multiline: true,
        confirmLabel: 'Reject document',
        tone: 'danger',
      });
      if (reason === null) return;
    }
    setDrawerBusy(true);
    try {
      await api.post(`/admin/delivery-partners/${selected.id}/documents/${docId}/verify`, { verified, rejectionReason: reason });
      await refreshDetail(selected.id);
      toast.success(verified ? 'Document verified' : 'Document rejected');
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setDrawerBusy(false);
    }
  };

  const act = async (action: 'approve' | 'reject' | 'suspend' | 'reactivate') => {
    if (!selected) return;
    let body: Record<string, unknown> | undefined;
    if (action === 'reject') {
      const reason = await prompt({
        title: 'Reject partner?',
        description: `${selected.fullName} is told why by SMS and in the app, and can update their details and resubmit.`,
        label: 'Reason for rejection',
        placeholder: 'e.g. Documents do not match the name on the account',
        required: true,
        multiline: true,
        confirmLabel: 'Reject partner',
        tone: 'danger',
      });
      if (reason === null) return;
      body = { reason };
    }
    if (action === 'suspend') {
      const ok = await confirm({
        title: 'Suspend partner?',
        description: `${selected.fullName} can no longer go online or take delivery jobs. You can reactivate them at any time.`,
        confirmLabel: 'Suspend partner',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setDrawerBusy(true);
    try {
      await api.post(`/admin/delivery-partners/${selected.id}/${action}`, body);
      toast.success(`Partner ${action}d`);
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setDrawerBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const searching = debouncedQ.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Delivery partners" description="KYC verification, approvals and partner roster" />

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatus(t.key)}
            aria-pressed={status === t.key}
            className="fchip"
          >
            {t.label}
          </button>
        ))}
      </div>

      <TableSearch
        value={q}
        onChange={setQ}
        placeholder="Search by name, phone, vehicle, KYC, availability or rating"
        resultCount={!loading && searching ? total : undefined}
      />

      <div className="panel overflow-hidden">
        {loading ? (
          <SkeletonRows cols={5} />
        ) : rows.length === 0 && searching ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for “${debouncedQ}”`}
            body="Try another word, or clear the search to see every partner in this view."
            action={<button type="button" onClick={() => setQ('')} className="btn btn-secondary">Clear search</button>}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="two_wheeler"
            title="No partners in this view"
            body="Partners appear here once they register and submit their KYC documents."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Phone</th>
                  <th>Vehicle</th>
                  <th>KYC</th>
                  <th>Availability</th>
                  <th>Rating</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-admin-soft text-[12.5px] font-bold text-admin-ink">{initials(r.fullName)}</span>
                        <span className="font-semibold text-ink-900">{r.fullName}</span>
                      </div>
                    </td>
                    <td className="sku text-ink-700">{r.phoneE164}</td>
                    <td className="text-ink-700">{r.vehicleType ?? '—'}</td>
                    <td><KycBadge status={r.kycStatus} /></td>
                    <td><StatusBadge status={r.availabilityStatus} /></td>
                    <td>
                      <span className="flex items-center gap-1 font-tabular text-ink-700"><Star size={11} className="fill-haldi-500 text-haldi-500" />{r.ratingAvg.toFixed(1)} ({r.ratingCount})</span>
                    </td>
                    <td className="text-right">
                      <button type="button" onClick={() => void openDetail(r.id)} className="btn btn-secondary btn-sm">Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={total}
            perPage={PER_PAGE}
            onPageChange={setPage}
          />
        )}
      </div>

      {/* The delivery area is a radius now — set it in Platform Config → Delivery. */}
      <LiveOpsMap />

      {selected && (
        <DetailDrawer
          partner={selected}
          busy={drawerBusy}
          onClose={() => setSelected(null)}
          onVerifyDoc={verifyDoc}
          onAct={act}
        />
      )}
    </div>
  );
}

function KycBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    APPROVED: 'badge-success',
    SUBMITTED: 'badge-warning',
    PENDING: '',
    REJECTED: 'badge-danger',
  };
  return <span className={`badge ${map[status] ?? ''}`}>{sentenceCase(status)}</span>;
}

function DetailDrawer({
  partner, busy, onClose, onVerifyDoc, onAct,
}: {
  partner: PartnerDetail;
  busy: boolean;
  onClose: () => void;
  onVerifyDoc: (docId: string, verified: boolean) => void;
  onAct: (action: 'approve' | 'reject' | 'suspend' | 'reactivate') => void;
}) {
  // A partner can only be approved once every uploaded KYC document is verified
  // (and there is at least one). Mirrors the server-side guard in
  // adminDeliveryService.approve.
  const allDocsVerified = partner.documents.length > 0 && partner.documents.every((d) => d.verified);
  const pendingReview = partner.kycStatus === 'SUBMITTED' || partner.kycStatus === 'PENDING';
  const hasActions = pendingReview || partner.kycStatus === 'APPROVED';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-scrim">
      <div className="flex h-full w-[min(560px,100vw)] flex-col bg-surface-card shadow-elev-4">
        <div className="flex items-start justify-between gap-3 border-b border-surface-line px-6 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className="font-display text-drawer-title text-ink-900">{partner.fullName}</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-500">{partner.phoneE164}{partner.email ? ` · ${partner.email}` : ''}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="btn btn-quiet btn-icon btn-sm"><X size={18} /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div className="mb-6 grid grid-cols-2 gap-4">
            <Info label="Vehicle" value={partner.vehicleType ?? '—'} />
            <Info label="KYC" value={sentenceCase(partner.kycStatus)} />
            <Info label="Availability" value={sentenceCase(partner.availabilityStatus)} />
            <Info label="Active" value={partner.isActive ? 'Yes' : 'No'} />
            <Info label="Service pincodes" value={partner.servicePincodes?.length ? partner.servicePincodes.join(', ') : '—'} />
          </div>

          <section className="mb-6">
            <h3 className="mb-2.5 text-[15px] font-semibold text-ink-900">Documents</h3>
            {partner.documents.length === 0 ? (
              <p className="text-[13.5px] text-ink-500">No documents uploaded.</p>
            ) : (
              <div className="space-y-2">
                {partner.documents.map((d) => (
                  <div key={d.id} className="card flex items-center justify-between gap-3 p-3.5">
                    <div className="min-w-0">
                      <a href={documentHref(d.fileUrl)} target="_blank" rel="noreferrer" title="Private link, valid for 10 minutes — reopen the partner to refresh" className="text-[14px] font-semibold text-admin-ink hover:underline">{d.docType}</a>
                      <p className="text-[12.5px] text-ink-500">{d.verified ? 'Verified' : d.rejectionReason ? `Rejected: ${d.rejectionReason}` : 'Pending'}</p>
                    </div>
                    {d.verified ? (
                      // Once verified, no reject/re-verify action — the verdict is locked in.
                      <span className="badge badge-success"><CheckCircle size={12} /> Verified</span>
                    ) : (
                      <div className="flex gap-1.5">
                        <button type="button" disabled={busy} onClick={() => onVerifyDoc(d.id, true)} className="btn btn-quiet btn-icon btn-sm text-success-ink" title="Verify" aria-label="Verify document"><FileCheck size={16} /></button>
                        <button type="button" disabled={busy} onClick={() => onVerifyDoc(d.id, false)} className="btn btn-quiet btn-icon btn-sm text-danger-ink" title="Reject" aria-label="Reject document"><FileX size={16} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="mb-2.5 text-[15px] font-semibold text-ink-900">Bank accounts</h3>
            {partner.bankAccounts.length === 0 ? (
              <p className="text-[13.5px] text-ink-500">No bank account on file.</p>
            ) : (
              <div className="space-y-2">
                {partner.bankAccounts.map((b) => (
                  <div key={b.id} className="card p-3.5 text-[13.5px] text-ink-700">
                    {b.accountHolderName} · <span className="sku">••••{b.accountNumberLast4}</span> · <span className="sku">{b.ifsc}</span>{b.bankName ? ` · ${b.bankName}` : ''} {b.isPrimary && <span className="badge badge-accent ml-1">Primary</span>}
                  </div>
                ))}
              </div>
            )}
          </section>

          {pendingReview && !allDocsVerified && (
            <div className="alert alert-warning mt-6 text-[13.5px]">
              <FileX size={16} className="mt-0.5 shrink-0" />
              <span>
                {partner.documents.length === 0
                  ? 'This partner has no KYC documents to verify.'
                  : 'Verify every document before you can approve this partner.'}
              </span>
            </div>
          )}
        </div>

        {hasActions && (
          <div className="flex flex-wrap justify-end gap-2.5 border-t border-surface-line px-6 py-3.5">
            {pendingReview && (
              <>
                <button type="button" disabled={busy} onClick={() => onAct('reject')} className="btn btn-danger-outline"><XCircle size={16} /> Reject</button>
                <button
                  type="button"
                  disabled={busy || !allDocsVerified}
                  onClick={() => onAct('approve')}
                  title={allDocsVerified ? undefined : 'Verify all documents first'}
                  className="btn btn-success"
                ><CheckCircle size={16} /> Approve</button>
              </>
            )}
            {partner.kycStatus === 'APPROVED' && (
              partner.isActive ? (
                <button type="button" disabled={busy} onClick={() => onAct('suspend')} className="btn btn-danger-outline">Suspend</button>
              ) : (
                <button type="button" disabled={busy} onClick={() => onAct('reactivate')} className="btn btn-success">Reactivate</button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[12.5px] font-semibold text-ink-500">{label}</p>
      <p className="mt-0.5 text-[14px] text-ink-900">{value}</p>
    </div>
  );
}
